import React, { useState, useEffect } from "react";
import { Ticket, Plus, Copy, CheckCircle, XCircle, Loader2, Trash2 } from "lucide-react";
import Button from "../ui/Button";
import Badge from "../ui/Badge";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/Card";

const RedeemCodesTab = () => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [generatedCodes, setGeneratedCodes] = useState([]);
  const [showForm, setShowForm] = useState(false);
  
  const [codeForm, setCodeForm] = useState({
    product_type: "cartomancer_basic",
    quantity: 1,
    max_uses: 1,
    expires_in_days: null,
    access_duration_days: null,
    note: ""
  });

  const [accessDurationPreset, setAccessDurationPreset] = useState("permanent");
  const [customAccessDays, setCustomAccessDays] = useState("");

  useEffect(() => {
    fetchCodes();
  }, []);

  const fetchCodes = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/redeem-codes/list?limit=100`,
        { credentials: "include" }
      );
      if (response.ok) {
        const data = await response.json();
        setCodes(data.codes || []);
      }
    } catch (error) {
      console.error("Failed to fetch codes:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateCodes = async (e) => {
    e.preventDefault();
    try {
      setGenerating(true);
      // Resolve access_duration_days from preset selection
      let accessDays = null;
      if (accessDurationPreset === "custom") {
        accessDays = customAccessDays ? parseInt(customAccessDays) : null;
      } else if (accessDurationPreset !== "permanent") {
        accessDays = parseInt(accessDurationPreset);
      }

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/redeem-codes/generate`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            ...codeForm,
            expires_in_days: codeForm.expires_in_days ? parseInt(codeForm.expires_in_days) : null,
            access_duration_days: accessDays,
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        setGeneratedCodes(data.codes);
        alert(`✅ ${data.count} codici generati con successo!`);
        fetchCodes();
        setShowForm(false);
        setCodeForm({
          product_type: "cartomancer_basic",
          quantity: 1,
          max_uses: 1,
          expires_in_days: null,
          access_duration_days: null,
          note: ""
        });
        setAccessDurationPreset("permanent");
        setCustomAccessDays("");
      } else {
        const error = await response.json();
        alert(`❌ Errore: ${formatDetail(error.detail)}`);
      }
    } catch (error) {
      alert("❌ Errore di rete");
    } finally {
      setGenerating(false);
    }
  };

  const formatDetail = (detail) => {
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
      return detail
        .map((d) => (d && typeof d === "object" ? (d.msg || JSON.stringify(d)) : String(d)))
        .join(" · ");
    }
    if (detail && typeof detail === "object") return detail.msg || JSON.stringify(detail);
    return "Errore sconosciuto";
  };

  const handleDeactivateCode = async (codeHash, codeDisplay) => {
    if (!window.confirm(`Disattivare il codice ${codeDisplay}?`)) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/redeem-codes/${codeHash}`,
        {
          method: "DELETE",
          credentials: "include",
        }
      );

      if (response.ok) {
        alert("✅ Codice disattivato!");
        fetchCodes();
      } else {
        const error = await response.json();
        alert(`❌ Errore: ${formatDetail(error.detail)}`);
      }
    } catch (error) {
      alert("❌ Errore di rete");
    }
  };

  const handlePurgeInactive = async () => {
    if (!window.confirm(
      "Cancellare tutti i codici disattivati o già utilizzati?\n\nQuesta azione è irreversibile.",
    )) return;
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/redeem-codes/purge-inactive`,
        { method: "POST", credentials: "include" },
      );
      const data = await response.json();
      if (response.ok) {
        alert(`✅ ${data.deleted} codici cancellati.`);
        fetchCodes();
      } else {
        alert(`❌ Errore: ${formatDetail(data.detail)}`);
      }
    } catch {
      alert("❌ Errore di rete");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert("📋 Codice copiato negli appunti!");
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Loader2 className="w-12 h-12 text-gold animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Caricamento codici...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Generated Codes Display */}
      {generatedCodes.length > 0 && (
        <Card className="border-gold">
          <CardHeader>
            <CardTitle className="text-gold">🎉 Codici Generati!</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {generatedCodes.map((codeData, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gold/10 rounded-lg border border-gold">
                  <div>
                    <p className="font-mono text-lg text-gold">{codeData.code}</p>
                    <p className="text-xs text-muted-foreground">{codeData.product}</p>
                  </div>
                  <Button
                    onClick={() => copyToClipboard(codeData.code)}
                    variant="gold"
                    size="sm"
                  >
                    <Copy className="w-4 h-4 mr-1" />
                    Copia
                  </Button>
                </div>
              ))}
            </div>
            <Button
              onClick={() => setGeneratedCodes([])}
              variant="ghost"
              size="sm"
              className="mt-4"
            >
              Chiudi
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Generate Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center">
              <Ticket className="w-5 h-5 mr-2 text-gold" />
              Genera Codici Redeem
            </span>
            <Button
              onClick={() => setShowForm(!showForm)}
              variant={showForm ? "ghost" : "primary"}
              size="sm"
            >
              {showForm ? "Annulla" : <><Plus className="w-4 h-4 mr-1" /> Nuovo Codice</>}
            </Button>
          </CardTitle>
        </CardHeader>
        
        {showForm && (
          <CardContent>
            <form onSubmit={handleGenerateCodes} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-heading text-foreground mb-2">
                    Prodotto
                  </label>
                  <select
                    value={codeForm.product_type}
                    onChange={(e) => setCodeForm({ ...codeForm, product_type: e.target.value })}
                    className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:border-gold focus:outline-none"
                    data-testid="code-product-type-select"
                  >
                    <option value="cartomancer_basic">Cartomancer Basic </option>
                    <option value="cartomancer_premium">Cartomancer Premium </option>
                    <option value="premium_upgrade">Premium (solo funzionalità Premium temporanee)</option>
                    <option value="mytharion_vtt_beta">MytharionVTT Beta Access</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-heading text-foreground mb-2">
                    Quantità (max 100)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    value={codeForm.quantity}
                    onChange={(e) => setCodeForm({ ...codeForm, quantity: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:border-gold focus:outline-none"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-heading text-foreground mb-2">
                    Usi Massimi (0 = illimitati)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={codeForm.max_uses}
                    onChange={(e) => setCodeForm({ ...codeForm, max_uses: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:border-gold focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-heading text-foreground mb-2">
                    Scadenza (giorni, lascia vuoto = mai)
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={codeForm.expires_in_days || ""}
                    onChange={(e) => setCodeForm({ ...codeForm, expires_in_days: e.target.value })}
                    placeholder="30"
                    className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:border-gold focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-heading text-foreground mb-2">
                  Durata Accesso (dopo il riscatto)
                </label>
                <div className="grid grid-cols-4 gap-2 mb-2">
                  {[
                    { value: "permanent", label: "Permanente" },
                    { value: "1", label: "1 giorno" },
                    { value: "7", label: "7 giorni" },
                    { value: "30", label: "30 giorni" },
                    { value: "60", label: "60 giorni" },
                    { value: "90", label: "90 giorni" },
                    { value: "365", label: "365 giorni" },
                    { value: "custom", label: "Personalizzato" },
                  ].map((p) => (
                    <button
                      key={p.value}
                      type="button"
                      onClick={() => setAccessDurationPreset(p.value)}
                      data-testid={`access-duration-${p.value}`}
                      className={`px-3 py-2 rounded-lg text-xs font-heading border transition-colors ${
                        accessDurationPreset === p.value
                          ? "bg-gold text-background border-gold"
                          : "bg-background text-foreground border-border hover:border-gold"
                      }`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
                {accessDurationPreset === "custom" && (
                  <input
                    type="number"
                    min="1"
                    value={customAccessDays}
                    onChange={(e) => setCustomAccessDays(e.target.value)}
                    placeholder="Numero di giorni"
                    className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:border-gold focus:outline-none"
                    data-testid="access-duration-custom-input"
                  />
                )}
                <p className="text-xs text-muted-foreground mt-2">
                  {accessDurationPreset === "permanent"
                    ? "L'utente riceverà accesso permanente al prodotto."
                    : `L'utente riceverà accesso per ${
                        accessDurationPreset === "custom" ? customAccessDays || "?" : accessDurationPreset
                      } giorni dopo il riscatto.`}
                </p>
              </div>

              <div>
                <label className="block text-sm font-heading text-foreground mb-2">
                  Note (opzionale)
                </label>
                <input
                  type="text"
                  value={codeForm.note}
                  onChange={(e) => setCodeForm({ ...codeForm, note: e.target.value })}
                  placeholder="es: Promo YouTube, Giveaway Instagram..."
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:border-gold focus:outline-none"
                />
              </div>

              <Button
                type="submit"
                variant="primary"
                size="lg"
                className="w-full"
                disabled={generating}
              >
                {generating ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Generazione...
                  </>
                ) : (
                  <>
                    <Plus className="w-5 h-5 mr-2" />
                    Genera {codeForm.quantity} Codice{codeForm.quantity > 1 ? "i" : ""}
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        )}
      </Card>

      {/* Codes List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Codici Esistenti ({codes.length})</span>
            <Button
              onClick={handlePurgeInactive}
              variant="ghost"
              size="sm"
              className="text-ruby hover:bg-ruby/10"
              data-testid="btn-purge-inactive-codes"
              title="Cancella tutti i codici disattivati o già utilizzati"
            >
              <Trash2 className="w-4 h-4 mr-1.5" />
              Azzera Codici Usati/Disattivati
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {codes.map((code) => {
              const fullCode = code.code_plain || code.code_display; // fallback for legacy codes
              return (
              <div
                key={code.code_hash}
                className="p-4 border border-border rounded-lg hover:border-gold/50 transition-colors"
              >
                <div className="flex items-start justify-between mb-2 gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <p className="font-mono text-lg text-gold break-all" data-testid={`code-plain-${code.code_hash}`}>
                        {fullCode}
                      </p>
                      {code.code_plain && (
                        <button
                          onClick={() => copyToClipboard(code.code_plain)}
                          className="p-1.5 rounded border border-gold/30 text-gold hover:bg-gold/10 hover:border-gold transition-colors"
                          title="Copia codice completo"
                          data-testid={`copy-code-${code.code_hash}`}
                        >
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <Badge variant={code.active ? "gold" : "ruby"}>
                        {code.active ? "Attivo" : "Disattivato"}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mb-1">{code.product_name}</p>
                    <div className="flex gap-4 text-xs text-muted-foreground flex-wrap">
                      <span>Usi: {code.uses_count}/{code.max_uses === 0 ? "∞" : code.max_uses}</span>
                      {code.expires_at && (
                        <span>Scade: {new Date(code.expires_at).toLocaleDateString("it-IT")}</span>
                      )}
                      <span>
                        Accesso: {code.access_duration_days ? `${code.access_duration_days} giorni` : "Permanente"}
                      </span>
                      <span>Tipo: {code.code_type || "product"}</span>
                      <span>Creato: {new Date(code.created_at).toLocaleDateString("it-IT")}</span>
                    </div>
                    {code.note && (
                      <p className="text-xs text-gold mt-2">📝 {code.note}</p>
                    )}
                  </div>

                  <div className="flex gap-2">
                    {code.active && (
                      <Button
                        onClick={() => handleDeactivateCode(code.code_hash, fullCode)}
                        variant="ghost"
                        size="sm"
                        className="text-ruby hover:bg-ruby/10"
                      >
                        <XCircle className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
              );
            })}

            {codes.length === 0 && (
              <div className="text-center py-12">
                <Ticket className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
                <p className="text-muted-foreground mb-4">Nessun codice generato</p>
                <Button onClick={() => setShowForm(true)} variant="primary">
                  <Plus className="w-4 h-4 mr-2" />
                  Crea Primo Codice
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default RedeemCodesTab;
